/**
 * EmbedStyleCard — Admin-Panel zur Anpassung der Iframe-Preisliste.
 *
 * - Globaler Scale: +/- Buttons (0.05-Schritte, 0.6–1.5) skalieren ALLE
 *   Embed-Werte gleichmäßig.
 * - Individuelle Slider für Card-Padding, Schriftgröße Preise, Buttons,
 *   Tab-Größe, Spalten-Mindestbreite, Grid-Gap.
 * - Live-Vorschau via Iframe-Reload nach Speichern.
 * - Reset-Button → entfernt alle Overrides (Defaults aktiv).
 *
 * Settings werden in `pricing_display_settings.embed_style` persistiert.
 * Public Pricing-Page liest sie und wandelt sie in CSS-Variablen um.
 */
import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { Plus, Minus, RotateCcw, Save, Sliders, Eye } from 'lucide-react';

// Defaults entsprechen den fixierten Werten in PublicPricingPage.jsx
// (`.is-embed`). Wenn der Admin einen Wert explizit setzt, gewinnt der;
// andernfalls greift `default × scale`.
const DEFAULTS = {
  card_padding: 16,
  card_radius: 12,
  card_name_size: 15,
  price_size: 30,
  price_unit_size: 11,
  feature_size: 11.5,
  button_size: 10.5,
  button_pad_y: 9,
  tab_size: 11.5,
  cat_heading_size: 10.5,
  grid_min_width: 200,
  grid_gap: 12,
  section_margin_bottom: 24,
};

const FIELD_LABELS = {
  card_padding: 'Karten-Innenabstand',
  card_radius: 'Karten-Rundung',
  card_name_size: 'Service-Name',
  price_size: 'Preis-Schrift',
  price_unit_size: 'Preis-Zusatzzeile',
  feature_size: 'Feature-Liste',
  button_size: '„Jetzt buchen"-Button',
  button_pad_y: 'Button-Höhe',
  tab_size: 'Tab-Leiste',
  cat_heading_size: 'Kategorie-Überschrift',
  grid_min_width: 'Karten-Mindestbreite',
  grid_gap: 'Karten-Abstand',
  section_margin_bottom: 'Kategorie-Abstand',
};

const SLIDER_RANGES = {
  card_padding: [6, 40, 1],
  card_radius: [0, 24, 1],
  card_name_size: [10, 28, 0.5],
  price_size: [16, 60, 1],
  price_unit_size: [8, 18, 0.5],
  feature_size: [9, 20, 0.5],
  button_size: [8, 18, 0.5],
  button_pad_y: [4, 20, 1],
  tab_size: [9, 18, 0.5],
  cat_heading_size: [8, 18, 0.5],
  grid_min_width: [140, 400, 5],
  grid_gap: [4, 40, 1],
  section_margin_bottom: [8, 60, 2],
};

const EmbedStyleCard = ({ API, BASE_URL, displaySettings, setDisplaySettings }) => {
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  // Lokaler Working-State, wird auf displaySettings synchronisiert
  const stored = displaySettings?.embed_style || {};
  const [scale, setScale] = useState(typeof stored.scale === 'number' ? stored.scale : 1);
  const [overrides, setOverrides] = useState(() => {
    const o = {};
    Object.keys(DEFAULTS).forEach(k => {
      if (typeof stored[k] === 'number') o[k] = stored[k];
    });
    return o;
  });

  useEffect(() => {
    const s = displaySettings?.embed_style || {};
    setScale(typeof s.scale === 'number' ? s.scale : 1);
    const o = {};
    Object.keys(DEFAULTS).forEach(k => {
      if (typeof s[k] === 'number') o[k] = s[k];
    });
    setOverrides(o);
  }, [displaySettings?.embed_style]);

  // Effektiver Wert eines Feldes — Override falls gesetzt, sonst default*scale
  const effective = useMemo(() => {
    const eff = {};
    Object.keys(DEFAULTS).forEach(k => {
      eff[k] = typeof overrides[k] === 'number'
        ? overrides[k]
        : Math.round((DEFAULTS[k] * scale) * 10) / 10;
    });
    return eff;
  }, [scale, overrides]);

  const handleScaleChange = (delta) => {
    const next = Math.round((scale + delta) * 100) / 100;
    if (next >= 0.6 && next <= 1.5) {
      setScale(next);
      // Wenn der Admin den globalen Scale ändert, werden bestehende
      // Overrides automatisch gelöscht (alle Felder folgen wieder dem Scale).
      setOverrides({});
    }
  };

  const updateField = (key, value) => {
    setOverrides(prev => ({ ...prev, [key]: value }));
  };

  const resetField = (key) => {
    setOverrides(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleReset = () => {
    setScale(1);
    setOverrides({});
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...(displaySettings || {}),
        embed_style: { scale, ...overrides },
      };
      await axios.put(`${API}/public/pricing/display-settings`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDisplaySettings(payload);
      toast.success('Iframe-Größeneinstellungen gespeichert');
      // Vorschau-Iframe neu laden
      setPreviewKey(k => k + 1);
    } catch (e) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="embed-style-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sliders className="w-4 h-4 text-amber-600" />
          Iframe-Größe anpassen
        </CardTitle>
        <CardDescription className="text-sm text-gray-500">
          Mit den Plus/Minus-Buttons skalierst du die Preisliste global. Für
          Feintuning kannst du jedes Element einzeln einstellen.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Globaler Scale */}
        <div className="rounded-lg border bg-amber-50/40 p-4">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-semibold">Globaler Zoom</Label>
            <span className="text-2xl font-bold text-amber-600 tabular-nums" data-testid="embed-scale-value">
              {Math.round(scale * 100)}%
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={() => handleScaleChange(-0.05)}
              disabled={scale <= 0.6}
              data-testid="embed-scale-minus"
              aria-label="Verkleinern"
            >
              <Minus className="w-4 h-4" />
            </Button>
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-500 transition-all"
                style={{ width: `${((scale - 0.6) / 0.9) * 100}%` }}
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => handleScaleChange(0.05)}
              disabled={scale >= 1.5}
              data-testid="embed-scale-plus"
              aria-label="Vergrössern"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Bereich: 60% – 150%. Schritte: 5%.
          </p>
        </div>

        {/* Per-Element-Einstellungen */}
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAdvanced(s => !s)}
            data-testid="embed-style-toggle-advanced"
          >
            {showAdvanced ? '▾' : '▸'} Einzelne Elemente anpassen
          </Button>
          {showAdvanced && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.keys(DEFAULTS).map(key => {
                const [min, max, step] = SLIDER_RANGES[key];
                const value = effective[key];
                const isOverridden = typeof overrides[key] === 'number';
                return (
                  <div key={key} className="rounded border bg-white p-3">
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs font-medium">{FIELD_LABELS[key]}</Label>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono text-gray-600">
                          {value}{key === 'grid_min_width' || key === 'grid_gap' || key === 'card_padding' || key === 'card_radius' || key === 'button_pad_y' || key === 'section_margin_bottom' ? 'px' : 'px'}
                        </span>
                        {isOverridden && (
                          <button
                            onClick={() => resetField(key)}
                            className="text-[10px] text-amber-600 hover:underline"
                            data-testid={`embed-reset-${key}`}
                          >
                            zurücksetzen
                          </button>
                        )}
                      </div>
                    </div>
                    <Slider
                      min={min}
                      max={max}
                      step={step}
                      value={[value]}
                      onValueChange={(arr) => updateField(key, arr[0])}
                      data-testid={`embed-slider-${key}`}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex gap-2 pt-2">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex-1"
            data-testid="save-embed-style-btn"
          >
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? 'Speichern…' : 'Größen speichern'}
          </Button>
          <Button
            variant="outline"
            onClick={handleReset}
            data-testid="reset-embed-style-btn"
          >
            <RotateCcw className="w-4 h-4 mr-1.5" />
            Zurücksetzen
          </Button>
        </div>

        {/* Live-Vorschau direkt im Card */}
        <div>
          <div className="flex items-center gap-2 mb-2 text-sm text-gray-600">
            <Eye className="w-4 h-4" /> Live-Vorschau (900px Iframe-Breite)
          </div>
          <div className="border rounded-lg overflow-hidden" style={{ height: 460, backgroundColor: '#000' }}>
            <iframe
              key={previewKey}
              src={`${BASE_URL}/preisliste?embed=true`}
              style={{ width: '100%', height: '100%', border: 'none', backgroundColor: '#000' }}
              title="Iframe-Größe Vorschau"
              data-testid="embed-style-preview-iframe"
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Nach dem Speichern aktualisiert sich die Vorschau automatisch.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default EmbedStyleCard;
